import React, { useState, useEffect } from 'react';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { pagosAPI } from '../services/api';

// Inicializar MercadoPago con la public key
const MP_PUBLIC_KEY = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;

if (MP_PUBLIC_KEY) {
  initMercadoPago(MP_PUBLIC_KEY, { locale: 'es-AR' });
}

/**
 * Componente de Checkout de MercadoPago
 * 
 * Props:
 * - preferenceId: ID de la preferencia de pago (opcional si se usa createPreference)
 * - createPreference: Función async que crea y retorna la preferencia
 * - onSuccess: Callback cuando el pago es exitoso
 * - onError: Callback cuando hay un error
 * - tipo: 'suscripcion' | 'usuarios' | 'curso' | 'documentos'
 * - monto: Monto a mostrar
 * - moneda: 'USD' | 'ARS'
 * - titulo: Título del pago
 * - descripcion: Descripción opcional
 */
const MercadoPagoCheckout = ({
  preferenceId: initialPreferenceId,
  createPreference,
  onSuccess,
  onError,
  tipo = 'suscripcion',
  monto,
  moneda = 'USD',
  titulo,
  descripcion,
  disabled = false,
  buttonText = 'Pagar con MercadoPago'
}) => {
  const [preferenceId, setPreferenceId] = useState(initialPreferenceId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mpReady, setMpReady] = useState(!!initialPreferenceId);

  useEffect(() => {
    // Si ya tenemos un preferenceId inicial, usarlo
    if (initialPreferenceId) {
      setPreferenceId(initialPreferenceId);
      setMpReady(true);
    }
  }, [initialPreferenceId]);

  const handleCreatePreference = async () => {
    if (!createPreference) {
      setError('No se proporcionó función para crear preferencia');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createPreference();
      
      if (result.preference_id) {
        setPreferenceId(result.preference_id);
        setMpReady(true);
      } else {
        throw new Error('No se recibió preference_id');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al crear preferencia de pago';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para los eventos del Wallet
  const handleOnReady = () => {
    console.log('MercadoPago Wallet ready');
  };

  const handleOnError = (error) => {
    console.error('MercadoPago Wallet error:', error);
    setError('Error al cargar el botón de pago');
    onError?.(error);
  };

  if (!MP_PUBLIC_KEY) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>MercadoPago no está configurado</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-teal-200 bg-gradient-to-br from-white to-teal-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="w-5 h-5 text-teal-600" />
          {titulo || 'Realizar Pago'}
        </CardTitle>
        {descripcion && (
          <p className="text-sm text-gray-500">{descripcion}</p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Resumen del pago */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total a pagar:</span>
            <span className="text-2xl font-bold text-teal-600">
              {moneda === 'USD' ? 'USD ' : 'ARS '}
              {typeof monto === 'number' ? monto.toLocaleString('es-AR', { 
                minimumFractionDigits: moneda === 'USD' ? 2 : 0,
                maximumFractionDigits: moneda === 'USD' ? 2 : 0
              }) : monto}
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Botón para crear preferencia o mostrar Wallet */}
        {!mpReady ? (
          <Button
            onClick={handleCreatePreference}
            disabled={loading || disabled}
            className="w-full bg-[#009EE3] hover:bg-[#007BB8] text-white py-4 md:py-6 text-base md:text-lg min-h-[50px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin flex-shrink-0" />
                <span className="truncate">Preparando pago...</span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span className="truncate">{buttonText}</span>
              </>
            )}
          </Button>
        ) : (
          <div className="mercadopago-wallet-container">
            <Wallet
              initialization={{ preferenceId: preferenceId }}
              onReady={handleOnReady}
              onError={handleOnError}
              customization={{
                texts: {
                  action: 'pay',
                  valueProp: 'security_details'
                },
                visual: {
                  buttonBackground: 'default',
                  borderRadius: '8px',
                  valuePropColor: 'grey'
                }
              }}
            />
          </div>
        )}

        {/* Info adicional */}
        <div className="flex items-center gap-2 text-xs text-gray-500 justify-center">
          <CheckCircle className="w-4 h-4" />
          <span>Pago seguro con MercadoPago</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default MercadoPagoCheckout;
